function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : ''
}

export function normalizeStringArray(value) {
  if (!Array.isArray(value)) return []
  return Array.from(new Set(value.map(item => normalizeString(item)).filter(Boolean)))
}

export function hasWildcardPermission(permissionCodes = []) {
  return normalizeStringArray(permissionCodes).includes('*')
}

export function hasPermission(permissionCodes = [], requiredPermissions = [], mode = 'all') {
  const normalizedRequired = normalizeStringArray(requiredPermissions)
  if (!normalizedRequired.length) return true

  const normalizedPermissions = normalizeStringArray(permissionCodes)
  if (normalizedPermissions.includes('*')) return true

  if (mode === 'any') {
    return normalizedRequired.some(code => normalizedPermissions.includes(code))
  }

  return normalizedRequired.every(code => normalizedPermissions.includes(code))
}

export function hasMenuAccess(menuKeys = [], requiredMenuKeys = [], mode = 'all') {
  const normalizedRequired = normalizeStringArray(requiredMenuKeys)
  if (!normalizedRequired.length) return true

  const normalizedMenuKeys = normalizeStringArray(menuKeys)
  if (mode === 'any') {
    return normalizedRequired.some(key => normalizedMenuKeys.includes(key))
  }

  return normalizedRequired.every(key => normalizedMenuKeys.includes(key))
}

export function canAccessRequirement(requirement = {}, authState = {}) {
  const {
    isSuperAdmin = false,
    permissionCodes = [],
    menuKeys = []
  } = authState

  const requiredPermissions = normalizeStringArray(
    requirement.permissionCodes || (requirement.permissionCode ? [requirement.permissionCode] : [])
  )
  const requiredMenuKeys = normalizeStringArray(
    requirement.menuKeys || (requirement.menuKey ? [requirement.menuKey] : [])
  )

  if (!hasMenuAccess(menuKeys, requiredMenuKeys, requirement.menuMode || 'all')) {
    return false
  }

  if (isSuperAdmin || hasWildcardPermission(permissionCodes)) {
    return true
  }

  if (!hasPermission(permissionCodes, requiredPermissions, requirement.permissionMode || 'all')) {
    return false
  }

  return true
}

export const ACCESS_REQUIREMENTS = {
  DASHBOARD: { menuKey: 'dashboard', permissionCode: 'dashboard.view' },
  USER: { menuKey: 'user', permissionCode: 'user.page' },
  HISTORY: { menuKey: 'history', permissionCode: 'history.page' },
  SENSITIVE: { menuKey: 'sensitive', permissionCode: 'sensitive.page' },
  BASIC: { menuKey: 'basic', permissionCode: 'basic.page' },
  KNOWLEDGE: { menuKey: 'knowledge', permissionCode: 'knowledge.page' },
  OFFICIAL_FILE: { menuKey: 'official-file', permissionCode: 'official_file.page' },
  RBAC: { menuKey: 'rbac', permissionCode: 'rbac.page' },
  USER_CREATE: { permissionCode: 'user.create' },
  USER_UPDATE: { permissionCode: 'user.update' },
  USER_DELETE: { permissionCode: 'user.delete' },
  USER_PASSWORD_RESET: { permissionCode: 'user.password.reset' },
  RBAC_ROLE_CREATE: { permissionCode: 'rbac.role.create' },
  RBAC_ROLE_UPDATE: { permissionCode: 'rbac.role.update' },
  RBAC_ROLE_DELETE: { permissionCode: 'rbac.role.delete' },
  RBAC_ROLE_BIND_PERMISSIONS: { permissionCode: 'rbac.role.bind_permissions' },
  RBAC_PERMISSION_CREATE: { permissionCode: 'rbac.permission.create' },
  RBAC_PERMISSION_UPDATE: { permissionCode: 'rbac.permission.update' },
  RBAC_PERMISSION_DELETE: { permissionCode: 'rbac.permission.delete' }
}

export const TOP_NAV_MENU_ITEMS = [
  { key: 'dashboard', index: '/dashboard', label: '仪表盘', icon: 'el-icon-s-home', ...ACCESS_REQUIREMENTS.DASHBOARD },
  { key: 'user', index: '/user', label: '用户管理', icon: 'el-icon-user', ...ACCESS_REQUIREMENTS.USER },
  { key: 'history', index: '/history', label: '历史会话', icon: 'el-icon-message', ...ACCESS_REQUIREMENTS.HISTORY },
  { key: 'sensitive', index: '/sensitive', label: '敏感词库', icon: 'el-icon-lock', ...ACCESS_REQUIREMENTS.SENSITIVE },
  { key: 'knowledge', index: '/knowledge', label: '知识库管理', icon: 'el-icon-reading', ...ACCESS_REQUIREMENTS.KNOWLEDGE },
  { key: 'official-file', index: '/official-file', label: '官方文件管理', icon: 'el-icon-document', ...ACCESS_REQUIREMENTS.OFFICIAL_FILE }
]

export const BASIC_MENU_ITEMS = [
  { key: 'basic-agreement', index: '/basic/agreement', label: '用户协议', ...ACCESS_REQUIREMENTS.BASIC },
  { key: 'basic-privacy', index: '/basic/privacy', label: '隐私协议', ...ACCESS_REQUIREMENTS.BASIC },
  { key: 'basic-about', index: '/basic/about', label: '关于我们', ...ACCESS_REQUIREMENTS.BASIC },
  { key: 'basic-customer-service', index: '/basic/customer-service', label: '客服管理', ...ACCESS_REQUIREMENTS.BASIC }
]

export const HIDDEN_ACCESS_ITEMS = [
  { key: 'rbac', index: '/rbac/permissions', label: 'RBAC管理', ...ACCESS_REQUIREMENTS.RBAC }
]

export const HOME_ROUTE_CANDIDATES = [
  ...TOP_NAV_MENU_ITEMS,
  ...BASIC_MENU_ITEMS,
  ...HIDDEN_ACCESS_ITEMS
]

export function resolveDefaultHomePath(authState = {}, preferredPath = '') {
  const normalizedPreferredPath = normalizeString(preferredPath)
  const normalizedMenuKeys = normalizeStringArray(authState.menuKeys)
  const menuDrivenCandidates = normalizedMenuKeys
    .map(menuKey => {
      if (menuKey === 'basic') return '/basic/agreement'
      if (menuKey === 'rbac') return '/rbac/permissions'
      const matchedItem = HOME_ROUTE_CANDIDATES.find(item => item.key === menuKey)
      return matchedItem ? matchedItem.index : ''
    })
    .filter(Boolean)

  const candidates = normalizedPreferredPath
    ? [normalizedPreferredPath, ...menuDrivenCandidates, ...HOME_ROUTE_CANDIDATES.map(item => item.index)]
    : [...menuDrivenCandidates, ...HOME_ROUTE_CANDIDATES.map(item => item.index)]

  const visited = new Set()

  for (const path of candidates) {
    const normalizedPath = normalizeString(path)
    if (!normalizedPath || visited.has(normalizedPath)) continue
    visited.add(normalizedPath)

    const item = HOME_ROUTE_CANDIDATES.find(menu => menu.index === normalizedPath)
    if (item && canAccessRequirement(item, authState)) {
      return normalizedPath
    }
  }

  return '/403'
}
